import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTable } from 'react-table';
import { useHiddenColumnsSetter } from '../../table/hooks/use-hidden-columns-setter';
import { getHiddenColumns } from '../../table/table-helpers/get-hidden-columns';
import { getRowId } from '../../table/table-helpers/get-row-id';
import { useAlerts } from '../../table/table-helpers/use-alerts';
import { useDecorators } from '../../table/table-helpers/use-decorators';
import { useExpandable } from '../../table/table-helpers/use-expandable';
import { useInfoIcons } from '../../table/table-helpers/use-info-icons';
import { useSelection } from '../../table/table-helpers/use-selection';
import { defaultSettingHooks } from './default-settings';
import { useDataGrid } from './use-data-grid';
const calculatePageCount = (totalItems, pageSize) => {
    if (totalItems === -1) {
        return -1;
    }
    else if (totalItems === 0) {
        return 1;
    }
    else {
        return Math.floor((totalItems - 1) / pageSize) + 1;
    }
};
const DEFAULT_PAGE_SIZE = 10;
export const useServerSideDataGrid = (params) => {
    const { columns, initialState, data, controlledState, selectable = false, expandable = false, alertIcon = undefined, infoIcons = undefined, totalItems = -1, tableOptions, layoutType = 'table', isOverrideRowSelect = true, isRowExpandable, skipHiddenColumnsSetter, decorators } = params;
    const [pageCount, setPageCount] = useState(calculatePageCount(totalItems, (initialState === null || initialState === void 0 ? void 0 : initialState.pageSize) || DEFAULT_PAGE_SIZE));
    const tableInstanceColumns = useMemo(() => [...columns], [columns]);
    const initial = useMemo(() => ({
        pageSize: DEFAULT_PAGE_SIZE,
        hiddenColumns: getHiddenColumns(columns),
        ...initialState
    }), [initialState, columns]);
    const tableInstance = useTable({
        columns: tableInstanceColumns,
        data,
        getRowId,
        pageCount,
        initialState: initial,
        useControlledState: (state) => React.useMemo(() => ({
            ...state,
            ...controlledState
        }), [state, controlledState]),
        autoResetPage: false,
        autoResetSortBy: false,
        autoResetSelectedRows: false,
        autoResetHiddenColumns: false,
        autoResetGroupBy: false,
        autoResetGlobalFilter: false,
        autoResetExpanded: false,
        autoResetFilters: false,
        autoResetRowState: false,
        manualPagination: true,
        manualGlobalFilter: true,
        manualSortBy: true,
        manualFilters: true,
        decorators,
        ...tableOptions
    }, ...defaultSettingHooks(layoutType, isOverrideRowSelect), useAlerts(alertIcon), useInfoIcons(infoIcons), useExpandable(expandable, isRowExpandable), useSelection(selectable), 
    // must be the last hook to be able to decorate columns added by other hooks
    useDecorators);
    useHiddenColumnsSetter(columns, tableInstance, skipHiddenColumnsSetter);
    useEffect(() => {
        setPageCount(calculatePageCount(totalItems, tableInstance.state.pageSize));
    }, [totalItems, tableInstance.state.pageSize]);
    const dataGrid = useDataGrid(layoutType);
    const { gotoPage, setColumnOrder, setSortBy, setGlobalFilter, setHiddenColumns, setAllFilters, toggleAllRowsExpanded } = tableInstance;
    const resetTable = useCallback((ignoreResetProperties) => {
        const { hiddenColumns, columnOrder } = ignoreResetProperties || {};
        gotoPage(0);
        if (!columnOrder) {
            setColumnOrder([]);
        }
        if (!hiddenColumns) {
            setHiddenColumns([]);
        }
        setSortBy([]);
        setGlobalFilter(undefined);
        setAllFilters([]);
        toggleAllRowsExpanded(false);
    }, [
        gotoPage,
        setColumnOrder,
        setSortBy,
        setGlobalFilter,
        setHiddenColumns,
        setAllFilters,
        toggleAllRowsExpanded
    ]);
    return { tableInstance, resetTable, dataGrid };
};
